import { useCallback } from 'react';
import { QueryClient, useQueryClient } from 'react-query';
import useAddFlexMatchPreferredEmployee from '../hooks/useAddFlexMatchPreferredEmployee';
import useRemoveFlexMatchPreferredEmployee from '../hooks/useRemoveFlexMatchPreferredEmployee';
import type { FlexMatchTaskResponse } from 'RestAPIs/flex-match/types';
import { Employee } from 'components/shared/EmployeeSelector';
import {
  CANDIDATES_QUERY_KEY,
  FLEX_TASKS_QUERY_KEY
} from './useFlexMatchSockets.consts';

const patchAllFlexCaches = (qc: QueryClient, updater: (g: any) => any) => {
  qc.setQueriesData({ queryKey: [FLEX_TASKS_QUERY_KEY] }, (old: any) =>
    old ? updater(old) : old
  );
};

export const usePreferredEmployeeActions = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: addPreferredEmployee } =
    useAddFlexMatchPreferredEmployee();
  const { mutateAsync: removePreferredEmployee } =
    useRemoveFlexMatchPreferredEmployee();

  const addEmployeeFor = useCallback(
    (row: FlexMatchTaskResponse | null, matchDrawerOpen: boolean) =>
      async (employee: Employee) => {
        if (!row) return;
        // optimistic patch
        if (!matchDrawerOpen) {
          patchAllFlexCaches(queryClient, (g: any) => {
            const idx = g.data.findIndex(
              (r: any) => r.requestId === row.requestId
            );
            if (idx === -1) return g;
            const next = g.data.slice();
            next[idx] = {
              ...next[idx],
              preferredEmployees: [
                ...(next[idx].preferredEmployees ?? []),
                { employeeId: employee.employeeId, createdBy: 'FLEX_PLANNER' }
              ]
            };
            return { ...g, data: next };
          });
        }

        try {
          return await addPreferredEmployee({
            requestId: row.requestId,
            body: {
              employeeId: employee.employeeId,
              createdBy: 'FLEX_PLANNER',
              bpmTaskId: row.taskId
            }
          });
        } catch (error) {
          throw error;
        } finally {
          if (matchDrawerOpen) {
            await queryClient.invalidateQueries([
              CANDIDATES_QUERY_KEY,
              row.requestId
            ]);
          }
        }
      },
    [addPreferredEmployee, queryClient]
  );

  const removeEmployeeFor = useCallback(
    (row: FlexMatchTaskResponse | null, matchDrawerOpen) =>
      async (employee: Employee) => {
        if (!row) return;
        // optimistic patch
        if (!matchDrawerOpen) {
          patchAllFlexCaches(queryClient, (g: any) => {
            const idx = g.data.findIndex(
              (r: any) => r.requestId === row.requestId
            );
            if (idx === -1) return g;
            const next = g.data.slice();
            next[idx] = {
              ...next[idx],
              preferredEmployees: (next[idx].preferredEmployees ?? []).filter(
                (e: any) => e.employeeId !== employee.employeeId
              )
            };
            return { ...g, data: next };
          });
        }

        try {
          return await removePreferredEmployee({
            requestId: row.requestId,
            body: {
              employeeId: employee.employeeId,
              bpmTaskId: row.taskId
            }
          });
        } catch (error) {
          throw error;
        } finally {
          if (matchDrawerOpen) {
            await queryClient.invalidateQueries([
              CANDIDATES_QUERY_KEY,
              row.requestId
            ]);
          }
        }
      },
    [removePreferredEmployee, queryClient]
  );

  return { addEmployeeFor, removeEmployeeFor };
};
